import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  createCustomerPortalSupportTicket,
  getCustomerPortalSupportTicketFormSchema,
  getCustomerPortalSupportTickets,
  getCustomerPortalTenants,
} from '../../api/customerPortalApi';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';
import { hasPermission } from '../../auth/permissions';
import { useAuth } from '../../auth/AuthContext';
import { DataTable } from '../../components/DataTable';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { TableToolbar } from '../../components/TableToolbar';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { Select } from '../../components/ui/Select';
import { TextInput } from '../../components/ui/TextInput';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import { portalSupportCreateControls } from '../../utils/portalSupportCapabilities';

const { semantic } = colorTokens;

const EMPTY_FORM = {
  form_type: '',
  tenant_id: '',
  theme: '',
  description: '',
  priority: 'normal',
  mask_number: '',
};

const dialogPaperSx = {
  width: 'min(560px, calc(100vw - 48px))',
  maxWidth: 'min(560px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

const numberSx = {
  fontFamily: typographyTokens.fontFamilyMono,
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: typographyTokens.fontWeightSemibold,
  color: semantic.text.primary,
  whiteSpace: 'nowrap',
};

const subjectSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const typeSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.secondary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function requiredLabel(label) {
  return `${label} *`;
}

export function CustomerPortalSupportPage() {
  const { t, i18n } = useTranslation(['support', 'common', 'statuses', 'billing']);
  const { session } = useAuth();
  const { activeCustomerId, loading: portalLoading } = useCustomerPortal();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [supportCapabilities, setSupportCapabilities] = useState({ can_create: false });
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const hasCreatePermission = hasPermission(session, 'customer.support.create', activeCustomerId);
  const createControls = portalSupportCreateControls({
    hasCreatePermission,
    canCreate: Boolean(supportCapabilities?.can_create),
  });
  const { showCreateAction, createEnabled, showInactiveSupportHint } = createControls;
  const emptyValue = t('common:empty_value');
  const recordCount = tickets.length;

  const selectedType = useMemo(() => {
    if (!schema?.types || !form.form_type) {
      return null;
    }
    return schema.types.find((type) => type.value === form.form_type) || null;
  }, [schema, form.form_type]);

  const loadTickets = useCallback(() => {
    if (!activeCustomerId) {
      return;
    }

    setLoading(true);
    setError('');

    Promise.all([
      getCustomerPortalSupportTickets(activeCustomerId),
      getCustomerPortalTenants(activeCustomerId).catch(() => ({ data: [] })),
    ])
      .then(([response, tenantsResponse]) => {
        setTickets(response.data || []);
        setSupportCapabilities(response.support_capabilities || { can_create: false });
        const tenantRows = tenantsResponse.data || tenantsResponse || [];
        setTenants(Array.isArray(tenantRows) ? tenantRows : []);
      })
      .catch(() => setError(t('support:load_tickets_failed')))
      .finally(() => setLoading(false));
  }, [activeCustomerId, t]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openCreateDialog = async () => {
    if (!createEnabled) {
      return;
    }

    const defaultTenantId = tenants.length === 1 ? String(tenants[0].id) : '';
    setOpen(true);
    setFormError('');
    setForm({
      ...EMPTY_FORM,
      tenant_id: defaultTenantId,
      priority: schema?.defaults?.priority || 'normal',
    });

    if (schema || !activeCustomerId) {
      return;
    }

    setSchemaLoading(true);
    try {
      const data = await getCustomerPortalSupportTicketFormSchema(activeCustomerId, {
        locale: i18n.language,
      });
      setSchema(data);
      setForm((prev) => ({
        ...prev,
        priority: data?.defaults?.priority || 'normal',
      }));
    } catch {
      setFormError(t('support:form.schema_load_failed'));
    } finally {
      setSchemaLoading(false);
    }
  };

  const handleTypeChange = (value) => {
    setForm((prev) => ({
      ...EMPTY_FORM,
      tenant_id: prev.tenant_id,
      form_type: value,
      priority: schema?.defaults?.priority || 'normal',
    }));
    setFormError('');
  };

  const isFieldRequired = (field) => Boolean(field?.required);

  const canSubmit = () => {
    if (!form.form_type || !selectedType || !form.tenant_id) {
      return false;
    }
    return (selectedType.fields || []).every((field) => {
      if (!isFieldRequired(field)) {
        return true;
      }
      const value = form[field.key];
      return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
    });
  };

  const handleCreate = async () => {
    if (!canSubmit()) {
      setFormError(t('support:form.select_type_first'));
      return;
    }

    setSubmitting(true);
    setFormError('');
    setError('');

    const formPayload = {};
    (selectedType.fields || []).forEach((field) => {
      const value = form[field.key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        formPayload[field.key] = typeof value === 'string' ? value.trim() : value;
      }
    });

    try {
      const ticket = await createCustomerPortalSupportTicket(activeCustomerId, {
        tenant_id: Number(form.tenant_id),
        form_type: form.form_type,
        form_payload: formPayload,
        type: form.form_type,
        theme: formPayload.theme || null,
        description: formPayload.description || null,
        mask_number: formPayload.mask_number || null,
        priority: formPayload.priority || schema?.defaults?.priority || 'normal',
      });
      setOpen(false);
      setForm({ ...EMPTY_FORM });
      navigate(`/portal/support/${ticket.id}`);
    } catch (err) {
      const message =
        err?.response?.data?.errors?.theme?.[0]
        || err?.response?.data?.errors?.tenant_id?.[0]
        || err?.response?.data?.errors?.form_type?.[0]
        || err?.response?.data?.errors?.form_payload?.[0]
        || err?.response?.data?.message;
      setFormError(message || t('support:portal_create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = useCallback((row) => {
    navigate(`/portal/support/${row.id}`);
  }, [navigate]);

  const columns = useMemo(() => [
    {
      id: 'public_number',
      label: t('billing:number'),
      width: 136,
      render: (row) => (
        <Typography component="span" sx={numberSx}>
          {row.public_number || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'subject',
      label: t('support:tickets.subject'),
      width: '28%',
      render: (row) => (
        <Typography component="span" sx={subjectSx}>
          {row.theme || row.subject || row.form_type || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'form_type',
      label: t('support:form.ticket_type'),
      width: '16%',
      render: (row) => (
        <Typography component="span" sx={typeSx}>
          {row.form_type || row.type || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      width: '14%',
      render: (row) => (
        row.status
          ? <StatusBadge status={row.status} category="support" size="Sm" />
          : emptyValue
      ),
    },
    {
      id: 'priority',
      label: t('support:tickets.priority'),
      width: '12%',
      render: (row) => (
        row.priority
          ? <StatusBadge status={row.priority} category="priority" size="Sm" />
          : emptyValue
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      width: 72,
      render: (row) => (
        <IconButton
          size="Sm"
          tone="Neutral"
          aria-label={t('support:portal.open_detail')}
          onClick={(event) => {
            event.stopPropagation();
            openTicket(row);
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      ),
    },
  ], [emptyValue, openTicket, t]);

  if (portalLoading) {
    return <LoadingSkeleton type="Detail" />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('support:portal.title')}
        subtitle={t('support:portal.list_subtitle')}
        actions={showCreateAction ? (
          <Button
            variant="Accent"
            onClick={openCreateDialog}
            disabled={!createEnabled}
            data-testid="portal-support-create"
          >
            {t('support:portal_new_ticket')}
          </Button>
        ) : null}
      />

      {showInactiveSupportHint ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="portal-support-create-disabled-hint">
          <Box component="div">{t('support:portal.active_support_required')}</Box>
          <Box component="div" sx={{ mt: 1 }}>{t('support:portal.security_incident_channel')}</Box>
        </Alert>
      ) : null}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadTickets} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('support:tickets.title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('support:portal.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton type="Table" embedded rows={4} columns={columns.length} />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={tickets}
            getRowKey={(row) => row.id}
            emptyMessage={t('support:portal.empty_list')}
            onRowClick={openTicket}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={open}
        onClose={() => {
          if (!submitting) setOpen(false);
        }}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 3,
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {t('support:portal_new_ticket')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, overflowY: 'auto' }}>
          {schemaLoading ? (
            <Typography
              sx={{
                py: 2,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.muted,
              }}
            >
              {t('support:form.loading_schema')}
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <Select
                density="Normal"
                fullWidth
                label={requiredLabel(t('support:portal.select_tenant'))}
                value={form.tenant_id}
                onChange={(e) => setForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name || tenant.slug || tenant.id}
                  </MenuItem>
                ))}
              </Select>

              <Select
                density="Normal"
                fullWidth
                label={requiredLabel(t('support:form.ticket_type'))}
                value={form.form_type}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {(schema?.types || []).map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>

              {!form.form_type && (
                <Alert severity="info" sx={{ bgcolor: semantic.bg.input }}>
                  {t('support:form.select_type_first')}
                </Alert>
              )}

              {selectedType?.help ? (
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: '18px',
                    color: semantic.text.secondary,
                  }}
                >
                  {selectedType.help}
                </Typography>
              ) : null}

              {selectedType && (selectedType.fields || []).map((field) => {
                const label = isFieldRequired(field)
                  ? requiredLabel(field.label)
                  : field.label;

                if (field.type === 'select') {
                  return (
                    <Select
                      key={field.key}
                      density="Normal"
                      fullWidth
                      label={label}
                      value={form[field.key] || ''}
                      onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                      helperText={field.help || undefined}
                    >
                      {(field.options || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  );
                }

                return (
                  <TextInput
                    key={field.key}
                    density="Normal"
                    label={label}
                    value={form[field.key] || ''}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    required={isFieldRequired(field)}
                    multiline={field.type === 'textarea'}
                    minRows={field.type === 'textarea' ? 4 : undefined}
                    placeholder={field.placeholder || undefined}
                    helperText={field.help || undefined}
                  />
                );
              })}

              {formError && (
                <Alert severity="error" sx={{ bgcolor: semantic.bg.input }}>
                  {formError}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="Ghost"
            disabled={submitting}
            onClick={() => setOpen(false)}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            loading={submitting}
            onClick={handleCreate}
            disabled={submitting || schemaLoading || !canSubmit()}
          >
            {submitting ? t('support:creating') : t('support:tickets.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
